import { memo, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Row, Col, Tag, Typography, Button } from "antd";
import {
  CalendarOutlined,
  CloudOutlined,
  RocketOutlined,
  DesktopOutlined,
  PlusOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import styled from "styled-components";
import { Organization_GetOne_Response, OrganizationAPIs } from "~/common/typescript/apis/OrganizationAPIs";
import dayjs from "dayjs";
import { UI_ScreenStyles } from "../UI_ScreenStyles";
import { createBreadcrumbItems, commonBreadcrumbs } from "~/utils/breadcrumbUtils";
import Routes from "~/routes/routes";

const { Title, Text } = Typography;

const OrganizationRegistrationScreen = () => {
  const { organizationID } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization_GetOne_Response>();

  const fetchData = async () => {
    if (organizationID) {
      try {
        const orgData = await OrganizationAPIs.remote_getOne(Number(organizationID));
        setOrganization(orgData);
      } catch (error) {
        console.error('Error fetching organization:', error);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [organizationID]);

  const getProductStatus = (expireAt?: string, blocked?: boolean) => {
    if (blocked) {
      return { status: 'Bị khóa', color: 'red', isRegistered: true };
    }
    if (!expireAt) {
      return { status: 'Chưa đăng ký', color: 'default', isRegistered: false };
    }

    const isExpired = dayjs(expireAt).isBefore(dayjs());
    if (isExpired) {
      return { status: 'Hết hạn', color: 'volcano', isRegistered: true };
    }
    return { status: 'Đang hoạt động', color: 'green', isRegistered: true };
  };

  const handleRegisterService = (serviceName: string) => {
    const registerUrl = 'https://tlgeo.xyz/support?action=register_wanted';
    window.open(registerUrl, '_blank');
  };

  const formatExpireDate = (expireAt?: string) => {
    if (!expireAt) return 'N/A';
    return dayjs(expireAt).format('DD/MM/YYYY');
  };

  const handleBack = () => {
    navigate(`/organization/${organizationID}`);
  };

  if (!organization) {
    return null;
  }

  return (
    <UI_ScreenStyles.UI_Wrapper>
      <UI_ScreenStyles.UI_Breadcrumb
        items={createBreadcrumbItems([
          commonBreadcrumbs.home,
          { title: 'Doanh nghiệp', href: Routes.ORGANIZATION, icon: commonBreadcrumbs.organizations.icon },
          { title: organization?.name || 'Chi tiết', href: `/organization/${organizationID}` },
          { title: 'Thông tin đăng ký' }
        ])}
      />

      <UI_ScreenStyles.UI_Content_Wrapper>
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ marginRight: 16 }}
            >
              Quay lại
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              Thông tin đăng ký sản phẩm - {organization.name}
            </Title>
          </div>

          <Row gutter={[16, 16]}>
            {/* Geocollect */}
            <Col span={24} md={8}>
              <UI_ProductCard>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CloudOutlined style={{ fontSize: 24, marginRight: 8, color: '#1890ff' }} />
                    <Text strong style={{ fontSize: 16 }}>Geocollect</Text>
                  </div>
                  {!getProductStatus(organization.gcExpireAt, organization.gcBlocked).isRegistered && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => handleRegisterService('Geocollect')}
                    >
                      Đăng ký
                    </Button>
                  )}
                </div>
                <div>
                  <Tag color={getProductStatus(organization.gcExpireAt, organization.gcBlocked).color}>
                    {getProductStatus(organization.gcExpireAt, organization.gcBlocked).status}
                  </Tag>
                </div>
                {organization.gcTier && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Gói: </Text>
                    <Tag color="processing">{organization.gcTier}</Tag>
                  </div>
                )}
                {organization.gcMaxNDevices && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Thiết bị: </Text>
                    <Text>{organization.gcMaxNDevices}</Text>
                  </div>
                )}
                {organization.gcExpireAt && (
                  <div style={{ marginTop: 8 }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    <Text type="secondary">Hết hạn: {formatExpireDate(organization.gcExpireAt)}</Text>
                  </div>
                )}
              </UI_ProductCard>
            </Col>

            {/* Flight Planner */}
            <Col span={24} md={8}>
              <UI_ProductCard>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <RocketOutlined style={{ fontSize: 24, marginRight: 8, color: '#52c41a' }} />
                    <Text strong style={{ fontSize: 16 }}>Flight Planner</Text>
                  </div>
                  {!getProductStatus(organization.fpExpireAt, organization.fpBlocked).isRegistered && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => handleRegisterService('Flight Planner')}
                    >
                      Đăng ký
                    </Button>
                  )}
                </div>
                <div>
                  <Tag color={getProductStatus(organization.fpExpireAt, organization.fpBlocked).color}>
                    {getProductStatus(organization.fpExpireAt, organization.fpBlocked).status}
                  </Tag>
                </div>
                {organization.fpTier && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Gói: </Text>
                    <Tag color="processing">{organization.fpTier}</Tag>
                  </div>
                )}
                {organization.fpMaxNDevices && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Thiết bị: </Text>
                    <Text>{organization.fpMaxNDevices}</Text>
                  </div>
                )}
                {organization.fpExpireAt && (
                  <div style={{ marginTop: 8 }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    <Text type="secondary">Hết hạn: {formatExpireDate(organization.fpExpireAt)}</Text>
                  </div>
                )}
              </UI_ProductCard>
            </Col>

            {/* Geodesktop */}
            <Col span={24} md={8}>
              <UI_ProductCard>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <DesktopOutlined style={{ fontSize: 24, marginRight: 8, color: '#722ed1' }} />
                    <Text strong style={{ fontSize: 16 }}>Geodesktop</Text>
                  </div>
                  {!getProductStatus(organization.gdExpireAt, organization.gdBlocked).isRegistered && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={() => handleRegisterService('Geodesktop')}
                    >
                      Đăng ký
                    </Button>
                  )}
                </div>
                <div>
                  <Tag color={getProductStatus(organization.gdExpireAt, organization.gdBlocked).color}>
                    {getProductStatus(organization.gdExpireAt, organization.gdBlocked).status}
                  </Tag>
                </div>
                {organization.gdTier && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Gói: </Text>
                    <Tag color="processing">{organization.gdTier}</Tag>
                  </div>
                )}
                {organization.gdMaxNDevices && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Thiết bị: </Text>
                    <Text>{organization.gdMaxNDevices}</Text>
                  </div>
                )}
                {organization.gdExpireAt && (
                  <div style={{ marginTop: 8 }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    <Text type="secondary">Hết hạn: {formatExpireDate(organization.gdExpireAt)}</Text>
                  </div>
                )}
              </UI_ProductCard>
            </Col>
          </Row>
        </Card>
      </UI_ScreenStyles.UI_Content_Wrapper>
    </UI_ScreenStyles.UI_Wrapper>
  );
};

export default memo(OrganizationRegistrationScreen);

const UI_ProductCard = styled.div`
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background-color: #fafafa;
  height: 100%;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;
